import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "erf_access_token";
let _accessToken = null;
try { _accessToken = localStorage.getItem(TOKEN_KEY); } catch (_) {}

export function setAccessToken(token) {
  _accessToken = token || null;
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch (_) {}
}
export function getAccessToken() { return _accessToken; }

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Always attach the token in a header — belt and braces alongside the httpOnly cookie.
api.interceptors.request.use((config) => {
  if (_accessToken) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${_accessToken}`;
    }
  }
  return config;
});

export function setImpersonationHeader(target) {
  if (target) api.defaults.headers.common["X-Impersonation-Target"] = target;
  else delete api.defaults.headers.common["X-Impersonation-Target"];
}

// Refresh on 401 (once); on 428 (password rotation required) redirect
let refreshing = null;
api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const original = err.config || {};
    const status = err.response?.status;
    if (status === 428) {
      const code = err.response?.data?.detail?.code || err.response?.data?.code;
      if (code === "mfa_enrollment_required" && typeof window !== "undefined" && !window.location.pathname.startsWith("/settings/security")) {
        window.location.replace("/settings/security");
      }
      if (code === "password_rotation_required" && typeof window !== "undefined") {
        if (!window.location.pathname.startsWith("/auth/rotate") &&
            !window.location.pathname.startsWith("/rotate-password")) {
          window.location.replace("/auth/rotate");
        }
      }
      return Promise.reject(err);
    }
    // MFA 401s (wrong code, step-up needed) are answers, not expired sessions: never refresh-and-replay them.
    const mfaAnswer = String(err.response?.data?.detail?.code || "").startsWith("mfa_");
    if (status === 401 && !mfaAnswer && !original._retried && !original.url?.includes("/auth/")) {
      original._retried = true;
      if (!refreshing) refreshing = api.post("/auth/refresh").finally(() => (refreshing = null));
      try {
        const rr = await refreshing;
        if (rr?.data?.access_token) setAccessToken(rr.data.access_token);
        return api(original);
      } catch (e) {
        return Promise.reject(err);
      }
    }
    return Promise.reject(err);
  }
);

// Paginated lists (backend/services/pagination.py, TRUNCATION_AUDIT (b)). When a GET answers X-Has-More: true, the
// client loads the following pages from X-Next-Offset and merges them (arrays, or {items: [...]}), so a list longer
// than one page is never silently cut off in the UI. After MAX_AUTO_PAGES it stops, keeps X-Has-More "true" and
// warns, so the remaining rows stay visible as "more" instead of disappearing.
export const MAX_AUTO_PAGES = 50;

export async function loadRemainingPages(r) {
  const cfg = r?.config || {};
  if ((cfg.method || "get").toLowerCase() !== "get" || cfg._pagedFollow || r.headers?.["x-has-more"] !== "true") return r;
  let data = r.data;
  let next = r.headers["x-next-offset"];
  let pages = 1;
  while (next && pages < MAX_AUTO_PAGES) {
    const nr = await api.request({ ...cfg, _pagedFollow: true, params: { ...(cfg.params || {}), offset: Number(next) } });
    if (Array.isArray(data) && Array.isArray(nr.data)) data = data.concat(nr.data);
    else if (Array.isArray(data?.items) && Array.isArray(nr.data?.items)) data = { ...data, items: data.items.concat(nr.data.items), page: nr.data.page };
    else break;
    next = nr.headers?.["x-has-more"] === "true" ? nr.headers["x-next-offset"] : null;
    pages += 1;
  }
  if (next) console.warn(`List ${cfg.url} has more than ${MAX_AUTO_PAGES} pages; showing the first ${MAX_AUTO_PAGES}.`);
  return { ...r, data, headers: { ...r.headers, "x-has-more": next ? "true" : "false", "x-next-offset": next || "" } };
}

api.interceptors.response.use((r) => loadRemainingPages(r));

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (typeof detail === "object" && detail.message) return detail.message;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
